// ── Design decisions ──
// Bloque de un paquete de la agencia: cada paquete lleva su PROPIO
// destinatario (ya no el del paso 1), su chip de caja, su conteo, su subtotal
// y su escalón de impuesto.
//
// · El nombre del destinatario se edita en línea (blur o Enter guardan); el
//   resto (teléfono, departamento/municipio, dirección, tipo de entrega) va
//   en un panel lateral chico para no engordar el bloque. El onClose del
//   Drawer es estable (useCallback sin deps) porque el Drawer re-enfoca su
//   panel cada vez que cambia esa identidad.
// · El chip de caja ES un select: "Sin caja" en ámbar cuando no está asignado,
//   y elegir una caja la asigna desde acá (la casilla en CajaCard hace lo
//   mismo al revés).
// · El escalón de impuesto sale de tierImpuesto (customsCalc): nunca un umbral
//   literal en este archivo.
// · Enter en Valor pide una fila nueva (onAddItem) y la enfoca en Cant; el
//   foco solicitado vive acá (tempId) y no en el padre.
// · Recibos, foto y "Analizar recibo con IA" quedan por paquete, en
//   PaqueteAdjuntos, igual que hoy.
// · La tarjeta es un contenedor CSS (`agency-pkg`, ver AgencyEncomienda.css):
//   la cabecera va en UNA línea cuando la tarjeta es ancha (≥ 720 px) y en dos
//   cuando es angosta; el texto de "Editar destinatario" se esconde en angosto
//   (queda el ícono con su aria-label). El conteo se pluraliza: "1 artículo".
// · El ícono de borrar paquete (PackageX) es distinto del de borrar fila
//   (Trash2) para que no se confundan.
// · Borrar paquete es destructivo: confirmación en línea en rojo, nunca un
//   window.confirm.

import { useCallback, useEffect, useRef, useState } from 'react';
import { PackageX, Pencil, Plus } from 'lucide-react';
import Drawer from '../ViajeroEncomienda/shared/Drawer';
import { SV_DEPARTAMENTOS } from '../../utils/svDepartamentos';
import { MAX_DESC, subtotalItems, tierImpuesto } from './agencyMapper';
import AgencyItemRow from './AgencyItemRow';
import PaqueteAdjuntos from './PaqueteAdjuntos';

const PAISES = [
  { code: '503', label: '+503' },
  { code: '1', label: '+1' },
];

const descomponerTelefono = (stored) => {
  const d = String(stored || '').replace(/\D/g, '');
  if (!d) return { country: '503', national: '' };
  if (d.length === 11 && d.startsWith('503')) return { country: '503', national: d.slice(3) };
  if (d.length === 11 && d.startsWith('1')) return { country: '1', national: d.slice(1) };
  if (d.length === 8) return { country: '503', national: d };
  if (d.length === 10) return { country: '1', national: d };
  return { country: '503', national: d };
};

const componerTelefono = (country, national) => {
  const nat = String(national || '').replace(/\D/g, '');
  if (!nat) return '';
  return nat.startsWith(country) && nat.length > country.length ? nat : country + nat;
};

const sumQty = (items) => (items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);

const esFilaVacia = (it) => !String(it.description || '').trim() && !(parseFloat(it.unitValue) > 0);

const PaqueteBlock = ({
  paquete,
  paquetes = [],
  cajas = [],
  customsConfig,
  api,
  encomiendaPublicId,
  saveStates = {},
  canDelete = true,
  focusTempId = null,
  onAddItem,
  onUpdateItem,
  onItemBlur,
  onRemoveItem,
  onMoveItem,
  onDeletePackage,
  onUpdateRecipient,
  onAssignPackageToCaja,
  onAnalyzeReceipt,
  buscarCatalogo,
}) => {
  const pkgId = paquete.id;
  const items = paquete.items || [];
  const recipient = paquete.recipient || {};

  const [nombre, setNombre] = useState(recipient.name || '');
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [form, setForm] = useState(null);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [focoTempId, setFocoTempId] = useState(null);

  useEffect(() => {
    setNombre(recipient.name || '');
  }, [recipient.name]);

  useEffect(() => {
    if (focusTempId) setFocoTempId(focusTempId);
  }, [focusTempId]);

  const cerrarRef = useRef(() => setDrawerAbierto(false));
  const cerrarDrawer = useCallback(() => cerrarRef.current(), []);

  const abrirDrawer = () => {
    const tel = descomponerTelefono(recipient.phone);
    setForm({
      country: tel.country,
      national: tel.national,
      address: recipient.address || '',
      departamento: recipient.departamento || '',
      municipio: recipient.municipio || '',
      delivery_type: recipient.delivery_type || 'Drop Off',
    });
    setDrawerAbierto(true);
  };

  const guardarNombre = () => {
    const limpio = nombre.trim();
    if (!limpio || limpio === (recipient.name || '')) {
      setNombre(recipient.name || '');
      return;
    }
    onUpdateRecipient && onUpdateRecipient(pkgId, { ...recipient, name: limpio });
  };

  const guardarDestinatario = () => {
    if (!form) return;
    onUpdateRecipient &&
      onUpdateRecipient(pkgId, {
        ...recipient,
        name: nombre.trim() || recipient.name || '',
        phone: componerTelefono(form.country, form.national),
        address: form.address.trim(),
        departamento: form.departamento,
        municipio: form.municipio,
        delivery_type: form.delivery_type,
      });
    setDrawerAbierto(false);
  };

  const otros = paquetes
    .filter((p) => p.id !== pkgId)
    .map((p) => ({ id: p.id, label: `Paquete ${p.package_number} — ${p.recipient?.name || 'Destinatario'}`, package_number: p.package_number }));
  const siguiente = otros.find((p) => p.package_number > paquete.package_number) || otros[0] || null;

  const agregarFila = () => {
    const tempId = onAddItem ? onAddItem(pkgId) : null;
    if (tempId) setFocoTempId(tempId);
  };

  const ligarCatalogo = (item, row) => {
    const patch = {
      description: String(row.descripcion || '').slice(0, MAX_DESC),
      description_raw: item.description_raw || item.description || '',
      catalog_id: row.id,
      cip_usd: row.cip_usd ?? null,
      unitValue: row.fob_usd != null ? String(row.fob_usd) : item.unitValue,
      unidad_medida: row.unidad_medida || item.unidad_medida || 'UNIDAD',
      catalogo_unit_type: row.unit_type || null,
      catalogo_unidad_medida: row.unidad_medida || null,
      catalogo_descripcion: row.descripcion || null,
      unit_type: row.unit_type || 'POR_UNIDAD',
      source: 'manual',
    };
    onUpdateItem && onUpdateItem(pkgId, item.tempId, patch);
    onItemBlur && onItemBlur(pkgId, { ...item, ...patch });
  };

  const desligarCatalogo = (item) => {
    const patch = {
      description: item.description_raw || item.description || '',
      catalog_id: null,
      cip_usd: null,
      catalogo_unit_type: null,
      catalogo_unidad_medida: null,
      catalogo_descripcion: null,
      unit_type: 'POR_UNIDAD',
    };
    onUpdateItem && onUpdateItem(pkgId, item.tempId, patch);
    onItemBlur && onItemBlur(pkgId, { ...item, ...patch });
  };

  const subtotal = subtotalItems(items);
  const tier = tierImpuesto(items, customsConfig);
  const conteo = sumQty(items);
  const cajaActual = cajas.find((c) => (c.packageIds || []).includes(pkgId)) || null;
  const cajaValor = paquete.cajaLocalId ?? cajaActual?.localId ?? '';
  const munis = SV_DEPARTAMENTOS.find((d) => d.name === form?.departamento)?.municipios || [];

  return (
    <section className="tile agency-pkg" aria-label={`Paquete ${paquete.package_number}`}>
      <header className="agency-pkg__header">
        <span className="badge badge-enc">Paquete {paquete.package_number}</span>
        <input
          type="text"
          className="input agency-pkg__nombre focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          aria-label="Nombre del destinatario"
          placeholder="Nombre del destinatario"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onBlur={guardarNombre}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
        <button
          type="button"
          onClick={abrirDrawer}
          aria-label={`Editar destinatario del paquete ${paquete.package_number}`}
          title="Editar teléfono, dirección y tipo de entrega"
          className="btn btn-secondary focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          style={{ padding: '6px 10px', minHeight: '36px' }}
        >
          <Pencil size={12} aria-hidden="true" /> <span className="agency-pkg__btn-texto">Editar destinatario</span>
        </button>
        <select
          aria-label="Caja del paquete"
          title="Asignar este paquete a una caja"
          className={`badge ${cajaValor ? 'badge-cas' : 'badge-hold'} focus-visible:ring-2 focus-visible:ring-[var(--amber)]`}
          style={{ cursor: 'pointer', border: `1px solid ${cajaValor ? 'var(--blue)' : 'var(--amber)'}`, padding: '3px 8px' }}
          value={cajaValor}
          onChange={(e) => onAssignPackageToCaja && onAssignPackageToCaja(pkgId, e.target.value || null)}
        >
          <option value="">Sin caja</option>
          {cajas.map((c) => (
            <option key={c.localId} value={c.localId}>
              Caja {c.caja_number}
            </option>
          ))}
        </select>
        <span className="badge badge-cas">{conteo} {conteo === 1 ? 'artículo' : 'artículos'}</span>
        <span className="metric-value" style={{ fontSize: '15px', marginTop: 0 }}>${subtotal.toFixed(2)}</span>
        <span
          className={`badge ${tier.sobreUmbral ? 'badge-hold' : 'badge-done'}`}
          title={tier.sobreUmbral ? 'Este paquete supera el umbral: paga DAI + IVA' : 'Este paquete paga solo IVA'}
        >
          {tier.etiqueta}
        </span>
        <span className="agency-pkg__acciones">
          {confirmarBorrar ? (
            <span className="flex items-center gap-1">
              <span className="text-[10px]" style={{ color: 'var(--avianca-red)' }}>
                ¿Eliminar paquete {paquete.package_number} y sus artículos?
              </span>
              <button
                type="button"
                onClick={() => onDeletePackage && onDeletePackage(pkgId)}
                aria-label={`Confirmar eliminación del paquete ${paquete.package_number}`}
                className="badge badge-error focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
                style={{ cursor: 'pointer', border: '1px solid var(--avianca-red)' }}
              >
                Sí, eliminar
              </button>
              <button
                type="button"
                onClick={() => setConfirmarBorrar(false)}
                aria-label="Mantener el paquete"
                className="btn btn-ghost focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                style={{ padding: '2px 6px', fontSize: '10px' }}
              >
                Mantener
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmarBorrar(true)}
              disabled={!canDelete}
              aria-label={`Eliminar paquete ${paquete.package_number}`}
              title={canDelete ? 'Eliminar paquete' : 'La encomienda necesita al menos un paquete'}
              className="btn-ghost focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
              style={{ padding: '6px', minWidth: '36px', minHeight: '36px', color: 'var(--avianca-red)' }}
            >
              <PackageX size={15} aria-hidden="true" />
            </button>
          )}
        </span>
      </header>

      <div className="agency-pkg__body">
        {items.length === 0 ? (
          <p className="empty-state" style={{ padding: '8px 0' }}>
            Este paquete no tiene artículos todavía.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <AgencyItemRow
                key={item.tempId}
                item={item}
                unitType={item.catalogo_unit_type || item.unit_type || 'POR_UNIDAD'}
                otrosPaquetes={otros}
                siguientePaqueteId={siguiente?.id ?? null}
                autoFocus={focoTempId === item.tempId ? 'cant' : null}
                saveState={saveStates[item.tempId] || null}
                buscarCatalogo={buscarCatalogo}
                onChange={(patch) => onUpdateItem && onUpdateItem(pkgId, item.tempId, patch)}
                onBlur={(patch) => onItemBlur && onItemBlur(pkgId, patch ? { ...item, ...patch } : item)}
                onEnterAtValor={agregarFila}
                onLinkCatalog={(row) => ligarCatalogo(item, row)}
                onUnlinkCatalog={() => desligarCatalogo(item)}
                onDelete={() => onRemoveItem && onRemoveItem(pkgId, item.tempId)}
                onMove={(target) => onMoveItem && onMoveItem(pkgId, item.tempId, target)}
                onEscapeEmpty={() => {
                  if (esFilaVacia(item) && !item.id) onRemoveItem && onRemoveItem(pkgId, item.tempId);
                }}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={agregarFila}
          aria-label={`Agregar artículo al paquete ${paquete.package_number}`}
          className="btn btn-ghost focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
          style={{ color: 'var(--avianca-red)', fontWeight: 'bold' }}
        >
          <Plus size={13} aria-hidden="true" /> Agregar artículo
        </button>

        <PaqueteAdjuntos paquete={paquete} encomiendaPublicId={encomiendaPublicId} api={api} onAnalyzeReceipt={onAnalyzeReceipt} />
      </div>

      <Drawer open={drawerAbierto} title={`Destinatario · Paquete ${paquete.package_number}`} onClose={cerrarDrawer} width="md">
        {form && (
          <div className="space-y-3">
            <div>
              <label className="metric-label mb-1 block" htmlFor={`dest-tel-${pkgId}`}>
                Teléfono
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select
                  className="input"
                  style={{ width: '90px', flex: '0 0 auto' }}
                  aria-label="Código de país"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                >
                  {PAISES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  id={`dest-tel-${pkgId}`}
                  type="tel"
                  className="input"
                  style={{ flex: 1 }}
                  aria-label="Teléfono del destinatario"
                  placeholder="7XXX-XXXX"
                  value={form.national}
                  onChange={(e) => setForm((f) => ({ ...f, national: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <select
                className="input"
                aria-label="Departamento"
                value={form.departamento}
                onChange={(e) => setForm((f) => ({ ...f, departamento: e.target.value, municipio: '' }))}
              >
                <option value="">-- Departamento --</option>
                {SV_DEPARTAMENTOS.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
              <select
                className="input"
                aria-label="Municipio"
                value={form.municipio}
                disabled={!form.departamento}
                onChange={(e) => setForm((f) => ({ ...f, municipio: e.target.value }))}
              >
                <option value="">-- Municipio --</option>
                {munis.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="text"
              className="input"
              aria-label="Dirección del destinatario"
              placeholder="Dirección completa"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            />
            <div className="flex gap-2" role="radiogroup" aria-label="Tipo de entrega">
              {['Pick Up', 'Drop Off'].map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={form.delivery_type === t}
                  onClick={() => setForm((f) => ({ ...f, delivery_type: t }))}
                  className={`btn ${form.delivery_type === t ? 'btn-primary' : 'btn-secondary'} focus-visible:ring-2 focus-visible:ring-[var(--blue)]`}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    background: form.delivery_type === t ? (t === 'Pick Up' ? 'var(--green)' : 'var(--avianca-red)') : '',
                  }}
                >
                  {t === 'Pick Up' ? '📦 Pick Up' : '🚚 Drop Off'}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={guardarDestinatario}
              aria-label="Guardar destinatario"
              className="btn btn-primary w-full focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
              style={{ justifyContent: 'center', minHeight: '44px' }}
            >
              Guardar destinatario
            </button>
          </div>
        )}
      </Drawer>
    </section>
  );
};

export default PaqueteBlock;
